import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';

import { ApplicationDemandDetail } from '../../../../Interfaces/api/applications/demand-detail.interface';
import { GroupDemandDetail } from '../../../../Interfaces/api/groups/demand-detail.interface';
import { ResourceStateService } from '../../../Shared/Services/resource-state.service';
import { DemandReportService } from '../Services/demand-report.service';
import { ReportDetailResponse } from '../Types/report-detail-response.type';
import { ReportSubjectTab } from '../Types/report-subject-tab.type';

@Injectable()
export class AllDemandDetailFacade {
  private readonly _reports = inject(DemandReportService);
  private readonly _resourceState = inject(ResourceStateService);

  private readonly _tab = signal<ReportSubjectTab | null>(null);
  private readonly _demandId = signal('');

  private readonly _demandResource = httpResource<ReportDetailResponse>(() => {
    const tab = this._tab();
    const id = this._demandId();
    return tab && id ? this._reports.show(tab, id) : undefined;
  });

  readonly demand: Signal<ApplicationDemandDetail | GroupDemandDetail | null> = computed(() => {
    const demand = this._resourceState.valueOf(this._demandResource)?.data;
    return demand && demand.id === this._demandId() ? demand : null;
  });

  readonly loading: Signal<boolean> = computed(() => this._demandResource.isLoading());

  private readonly _errorStatus = computed<number | null>(() => {
    const error = this._demandResource.error() as { status?: number } | undefined;
    return error ? (error.status ?? 0) : null;
  });

  readonly notFound: Signal<boolean> = computed(() => {
    const status = this._errorStatus();
    return status === 403 || status === 404;
  });

  readonly loadFailed: Signal<boolean> = computed(
    () => this._errorStatus() !== null && !this.notFound(),
  );

  setRoute(tab: ReportSubjectTab, demandId: string): void {
    const unchanged = this._tab() === tab && this._demandId() === demandId;
    this._tab.set(tab);
    this._demandId.set(demandId);
    if (unchanged) this.reload();
  }

  reset(): void {
    this._demandId.set('');
    this._tab.set(null);
  }

  reload(): void {
    this._demandResource.reload();
  }
}
